/**
 * Khối "Phòng Kinh doanh" trên phiếu lương (QĐ 0483, CEO 20a · 31a; bản mẫu ĐÃ DUYỆT NGUYÊN
 * 32a — GNPF/_giao-CEO/28-10-phieu-luong-phong-kd/). Hàm thuần dựng chữ cho từng dòng từ bản
 * tách máy chủ gửi (`salesTeam` trên phiếu). KHÔNG tự tính tiền: mọi số đọc thẳng từ máy chủ;
 * tổng là đúng phép cộng máy lương đặt vào dòng Hoa hồng (hoa hồng cũ + các khoản Phòng KD).
 */
import type { components } from "@/lib/api/generated/schema";
import { formatIntVN } from "@/lib/format";
import { MEMBER_KIND_LABEL, ROLE_LABEL, percentFromBp, shareLabel } from "@/features/sales-team/labels";

export type SalesTeamBreakdown = components["schemas"]["PayrollSalesTeamBreakdown"];
export type SalesTeamLine = components["schemas"]["SalesTeamLine"];
type Slip = components["schemas"]["PayrollSalesTeamSlip"];
type SlipSquad = components["schemas"]["SalesTeamSlipSquad"];

export type KdRowKey =
  | "commission"
  | "bonus"
  | "pocket"
  | "squadLead"
  | "teamLead"
  | "regionLead"
  | "quarter"
  | "reversal"
  | "legacy";

/** Một tiểu đội trong phần soi của dòng thưởng vượt / chia túi / thưởng tiểu đội trưởng. */
export type KdSquadNote = { squadId: string; name: string; text: string; amount?: number; kept?: boolean };

export type KdRow = {
  key: KdRowKey;
  label: string;
  caption: string;
  amount: number;
  /** Đơn để soi (hoa hồng tự bán, trừ huỷ / bỏ món). */
  lines: SalesTeamLine[];
  /** Tiểu đội để soi. */
  squads: KdSquadNote[];
};

/** Số tiền không kèm "đ" như bản mẫu; số âm dùng dấu trừ thật. */
export function amountText(n: number): string {
  return n < 0 ? `−${formatIntVN(-n)}` : formatIntVN(n);
}

/** "2026-11-02" ⇒ "02/11". */
export function dayMonth(day: string | undefined): string {
  if (!day) return "";
  const [, m = "", d = ""] = day.slice(0, 10).split("-");
  return `${d}/${m}`;
}

/** Ngày công 1/100 ngày ⇒ "30", "29,5". */
function workDays(w: number): string {
  return (w / 100).toLocaleString("vi-VN", { maximumFractionDigits: 2 });
}

function lastDayOf(month: string): string {
  const [y = 0, m = 1] = month.split("-").map(Number);
  return `${month}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, "0")}`;
}

/** Phần chia của người trong một dòng: "Trọn đơn", "Một nửa", "2/5"… */
export function lineShare(l: SalesTeamLine): string {
  const g = gcd(l.shareNum, l.shareDen);
  return shareLabel(l.shareNum / g, l.shareDen / g);
}

function gcd(a: number, b: number): number {
  return b === 0 ? Math.max(a, 1) : gcd(b, a % b);
}

function distinctOrders(lines: SalesTeamLine[]): number {
  return new Set(lines.map((l) => l.orderCode)).size;
}

/** Vai trên đầu khối: các quãng ngày cùng nhau gộp một nhóm (CSKH hai tiểu đội cả tháng). */
export type KdPositionGroup = {
  items: { role: string; unit: string; parents: string }[];
  period: string;
};

export function positionGroups(slip: Slip | undefined, month: string): KdPositionGroup[] {
  if (!slip) return [];
  const first = `${month}-01`;
  const last = lastDayOf(month);
  const groups: (KdPositionGroup & { key: string })[] = [];
  for (const p of slip.positions) {
    const key = `${p.from}|${p.to}`;
    let g = groups.find((x) => x.key === key);
    if (!g) {
      const period =
        p.from === first && p.to === last
          ? `cả tháng ${Number(month.slice(5, 7))}`
          : p.from === p.to
            ? `ngày ${dayMonth(p.from)}`
            : `${dayMonth(p.from)}–${dayMonth(p.to)}`;
      g = { key, items: [], period };
      groups.push(g);
    }
    g.items.push({
      role: ROLE_LABEL[p.role] ?? MEMBER_KIND_LABEL[p.role] ?? p.role,
      unit: p.unitName,
      parents: [p.teamName, p.regionName].filter(Boolean).join(" · "),
    });
  }
  return groups.map(({ items, period }) => ({ items, period }));
}

/**
 * Quý được bù ở tháng trả `month` (tháng đầu quý sau — QĐ 0483 mục 3.7): "2027-01" ⇒ "quý
 * 4/2026". Tháng khác ⇒ "".
 */
export function quarterLabel(month: string): string {
  const [y = 0, m = 0] = month.split("-").map(Number);
  if (![1, 4, 7, 10].includes(m)) return "";
  return m === 1 ? `quý 4/${y - 1}` : `quý ${(m - 1) / 3}/${y}`;
}

/** Tổng khối = đúng số máy lương cộng vào dòng Hoa hồng của dòng nhận. */
export function kdTotal(st: SalesTeamBreakdown): number {
  return (
    (st.legacyCommission ?? 0) +
    st.commission80 +
    st.bonus80 +
    st.pocketShare +
    st.squadLeadTier +
    st.teamLeadBonus +
    st.regionLeadBonus +
    st.quarterTopUp +
    st.reversal80
  );
}

/** Các dòng của khối theo thứ tự bản mẫu; dòng không có số và không có việc để soi thì bỏ. */
export function kdRows(st: SalesTeamBreakdown, month: string): KdRow[] {
  const slip = st.slip;
  const own = st.lines.filter((l) => !l.reversal);
  const back = st.lines.filter((l) => l.reversal);
  const squadOf = new Map<string, SlipSquad>((slip?.squads ?? []).map((s) => [s.squadId, s]));
  const keep = slip ? percentFromBp(slip.sellerKeepBp) : "";
  const rows: KdRow[] = [];
  const push = (row: KdRow, show: boolean) => {
    if (show) rows.push(row);
  };

  // Hoa hồng tự bán: phần 80% của từng đơn mình bán.
  const kinds = new Set(own.map((l) => l.kind));
  const rates = slip
    ? [kinds.has("sale") ? percentFromBp(slip.saleRateBp) : "", kinds.has("upsell") ? percentFromBp(slip.upsellRateBp) : ""]
        .filter(Boolean)
        .join(" / ")
    : "";
  const commissionCaption = [
    `${distinctOrders(own)} đơn`,
    rates && `${rates} tiền khách trả đủ`,
    keep && `bạn giữ ${keep}`,
    st.forfeitedKeep > 0 && `mất ${amountText(st.forfeitedKeep)} vì giấu khiếu nại`,
  ]
    .filter(Boolean)
    .join(" · ");
  push(
    { key: "commission", label: "Hoa hồng tự bán", caption: commissionCaption, amount: st.commission80, lines: own, squads: [] },
    own.length > 0 || st.commission80 !== 0,
  );

  // Thưởng vượt mục tiêu: theo tiểu đội của các đơn mình bán.
  const sellSquads = [...new Set(own.map((l) => l.squadId).filter((id): id is string => !!id))]
    .map((id) => squadOf.get(id))
    .filter((s): s is SlipSquad => !!s);
  const overText = (s: SlipSquad) =>
    !s.hasTarget
      ? "Tiểu đội chưa có mục tiêu tháng — chưa tính thưởng vượt"
      : s.overRatioBp > 0
        ? `Tiểu đội vượt mục tiêu ${percentFromBp(s.overRatioBp)} · chất lượng ${percentFromBp(s.qualityBp)}`
        : "Tiểu đội chưa vượt mục tiêu tháng";
  push(
    {
      key: "bonus",
      label: "Thưởng vượt mục tiêu",
      caption:
        sellSquads.length === 1
          ? [overText(sellSquads[0]!), sellSquads[0]!.overRatioBp > 0 && keep && `bạn giữ ${keep}`].filter(Boolean).join(" · ")
          : sellSquads.length > 1
            ? `${sellSquads.length} tiểu đội — bấm để xem từng tiểu đội`
            : "",
      amount: st.bonus80,
      lines: [],
      squads: sellSquads.map((s) => ({ squadId: s.squadId, name: s.name, text: overText(s) })),
    },
    st.bonus80 !== 0 || sellSquads.length > 0,
  );

  // Chia túi tiểu đội: một phần ở mỗi tiểu đội mình thuộc trong tháng.
  const pocketNotes: KdSquadNote[] = st.pocketLines.map((pl) => {
    const s = squadOf.get(pl.squadId);
    const text = pl.keptByFirm
      ? "Công ty giữ phần này — có lỗi được xác nhận trong tháng"
      : s
        ? `Túi mở ${amountText(s.pocketOpened)} · bạn ${workDays(pl.weight)} trên ${workDays(s.pocketWeight)} ngày công`
        : `Bạn ${workDays(pl.weight)} ngày công`;
    return { squadId: pl.squadId, name: s?.name ?? "", text, amount: pl.amount, kept: !!pl.keptByFirm };
  });
  push(
    {
      key: "pocket",
      label: "Chia túi tiểu đội",
      caption:
        pocketNotes.length === 1
          ? pocketNotes[0]!.text
          : pocketNotes.length > 1
            ? `${pocketNotes.length} tiểu đội — bấm để xem từng tiểu đội`
            : "",
      amount: st.pocketShare,
      lines: [],
      squads: pocketNotes,
    },
    st.pocketShare !== 0 || pocketNotes.length > 0,
  );

  // Thưởng quản lý: theo các quãng giữ chức trong tháng.
  const held = (role: string) => (slip?.positions ?? []).filter((p) => p.role === role);
  const ledSpans = held("squad_lead");
  const led = [...new Set(ledSpans.map((p) => p.unitId))]
    .map((id) => squadOf.get(id))
    .filter((s): s is SlipSquad => !!s);
  const wholeMonth = ledSpans.some((p) => p.from === `${month}-01` && p.to === lastDayOf(month));
  push(
    {
      key: "squadLead",
      label: "Thưởng tiểu đội trưởng",
      caption:
        led.length === 1
          ? [`Theo bậc doanh số tiểu đội ${amountText(led[0]!.realRevenue)}`, !wholeMonth && "phần những ngày bạn giữ chức"]
              .filter(Boolean)
              .join(" · ")
          : led.length > 1
            ? `${led.length} tiểu đội — bấm để xem từng tiểu đội`
            : "",
      amount: st.squadLeadTier,
      lines: [],
      squads: led.map((s) => ({
        squadId: s.squadId,
        name: s.name,
        text: `Doanh số tiểu đội ${amountText(s.realRevenue)} · thưởng cả tháng của tiểu đội ${amountText(s.squadLeadTier)}`,
      })),
    },
    st.squadLeadTier !== 0 || led.length > 0,
  );
  push(
    {
      key: "teamLead",
      label: "Thưởng đội trưởng",
      caption: slip ? `${percentFromBp(slip.teamLeadRateBp)} doanh thu thật các tiểu đội trong đội × mức chất lượng` : "",
      amount: st.teamLeadBonus,
      lines: [],
      squads: [],
    },
    st.teamLeadBonus !== 0 || held("team_lead").length > 0,
  );
  push(
    {
      key: "regionLead",
      label: "Thưởng leader miền",
      caption: slip ? `${percentFromBp(slip.regionLeadRateBp)} doanh thu thật cả miền × mức chất lượng` : "",
      amount: st.regionLeadBonus,
      lines: [],
      squads: [],
    },
    st.regionLeadBonus !== 0 || held("region_lead").length > 0,
  );

  // Bù thưởng vượt theo quý (CEO 3a): MỘT dòng, chỉ khi có khoản bù.
  const quarter = st.quarterLines ?? [];
  const quarterKeep = quarter.reduce((n, l) => n + l.keep, 0);
  const quarterPocket = quarter.reduce((n, l) => n + l.pocket, 0);
  const q = quarterLabel(month);
  push(
    {
      key: "quarter",
      label: q ? `Bù thưởng vượt ${q}` : "Bù thưởng vượt theo quý",
      caption: [
        "Xét lại thưởng vượt theo cả quý",
        quarterKeep !== 0 && `phần bạn bán ${amountText(quarterKeep)}`,
        quarterPocket !== 0 && `chia túi quý ${amountText(quarterPocket)}`,
      ]
        .filter(Boolean)
        .join(" · "),
      amount: st.quarterTopUp,
      lines: [],
      squads: [],
    },
    st.quarterTopUp !== 0,
  );

  // Trừ huỷ / bỏ món (CEO 33a): đúng phần 80% đã nhận ở tháng trước.
  push(
    {
      key: "reversal",
      label: "Trừ huỷ / bỏ món",
      caption: [`${distinctOrders(back)} đơn huỷ / bỏ món`, `trừ lại phần ${keep || "bạn"} đã nhận`].join(" · "),
      amount: st.reversal80,
      lines: back,
      squads: [],
    },
    back.length > 0 || st.reversal80 !== 0,
  );

  // Hoa hồng cũ: phần tính theo cách cũ vẫn nằm trong dòng Hoa hồng của tháng này.
  push(
    {
      key: "legacy",
      label: "Hoa hồng cũ",
      caption: "Đơn trả đủ trước 01/11/2026 hoặc lên trước khi bạn vào Phòng Kinh doanh — tính theo cách cũ",
      amount: st.legacyCommission ?? 0,
      lines: [],
      squads: [],
    },
    (st.legacyCommission ?? 0) !== 0,
  );
  return rows;
}
